export type ConversationTransportOptions =
  | { signedUrl: string; connectionType: "websocket" }
  | { conversationToken: string; connectionType: "webrtc" }
  | { agentId: string; connectionType: "webrtc" };

export type ConversationCredentials = {
  signedUrl?: string;
  conversationToken?: string;
};

export type TransportKind = "websocket" | "webrtc";

export function isFirefoxBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /\bfirefox\//i.test(navigator.userAgent);
}

/**
 * Ordered transports to try. Firefox historically had flaky long-lived WSS to
 * ElevenLabs (CloseEvent 1006 → "Echtzeitverbindung blockiert"); prefer WebRTC
 * token path first. Chrome/Safari keep signed WebSocket first (stable in prod).
 * Always keep a second path for automatic fallback.
 */
export function preferredTransportOrder(
  isFirefox: boolean = isFirefoxBrowser()
): TransportKind[] {
  return isFirefox ? ["webrtc", "websocket"] : ["websocket", "webrtc"];
}

/** Errors that justify trying the alternate realtime transport. */
export function isRealtimeConnectionError(error: unknown): boolean {
  const msg = String(
    (error as { message?: string })?.message ?? error ?? ""
  ).toLowerCase();
  return (
    msg.includes("websocket") ||
    msg.includes("network") ||
    msg.includes("closeevent") ||
    msg.includes("1006") ||
    msg.includes("socket error") ||
    msg.includes("closed by the server") ||
    msg.includes("failed to fetch") ||
    msg.includes("connection") ||
    msg.includes("realtime") ||
    msg.includes("ice") ||
    msg.includes("webrtc") ||
    msg.includes("timeout")
  );
}

export function getConversationTransportOptions(
  credentials: ConversationCredentials,
  _textOnly: boolean,
  _isFirefox: boolean = isFirefoxBrowser()
): ConversationTransportOptions {
  if (credentials.conversationToken) {
    return {
      conversationToken: credentials.conversationToken,
      connectionType: "webrtc",
    };
  }

  if (!credentials.signedUrl) {
    throw new Error("Missing conversation credentials");
  }

  // Never fall back to agentId-only WebRTC — that skips backend signing for
  // private agents. Signed WebSocket is the correct non-token path.
  return { signedUrl: credentials.signedUrl, connectionType: "websocket" };
}
